import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from './useAuth'

interface OnboardingStatus {
  onboarding_completed: boolean
  tour_completed: boolean
  monthly_goal: number | null
  hourly_rate: number | null
  weekly_work_hours: number | null
  tax_regime: string | null
}

export function useOnboardingStatus() {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['onboarding-status', user?.id],
    queryFn: async (): Promise<OnboardingStatus | null> => {
      if (!user?.id) return null

      const { data, error } = await supabase
        .from('profiles')
        .select('onboarding_completed, tour_completed, monthly_goal, hourly_rate, weekly_work_hours, tax_regime')
        .eq('id', user.id)
        .maybeSingle()

      if (error) throw error
      return data as OnboardingStatus | null
    },
    enabled: !!user?.id,
  })
}

export function useCompleteOnboarding() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation({
    mutationFn: async (data: {
      monthly_goal: number
      hourly_rate: number
      weekly_work_hours: number
      tax_regime: string
    }) => {
      if (!user?.id) throw new Error('User not authenticated')

      const { error } = await supabase
        .from('profiles')
        .update({
          ...data,
          onboarding_completed: true,
        })
        .eq('id', user.id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding-status', user?.id] })
      queryClient.invalidateQueries({ queryKey: ['profile', user?.id] })
    },
  })
}

export function useCompleteTour() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error('User not authenticated')

      const { error } = await supabase
        .from('profiles')
        .update({ tour_completed: true })
        .eq('id', user.id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding-status', user?.id] })
      queryClient.invalidateQueries({ queryKey: ['profile', user?.id] })
    },
  })
}

export function useResetTour() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error('User not authenticated')

      const { error } = await supabase
        .from('profiles')
        .update({ tour_completed: false })
        .eq('id', user.id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding-status', user?.id] })
      queryClient.invalidateQueries({ queryKey: ['profile', user?.id] })
    },
  })
}
